import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Stack,
  Button,
  Collapse,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CancelPresentation,
  PersonOff,
  SwapHoriz,
  AttachMoney,
  ExpandMore,
  ExpandLess,
  Warning,
  CheckCircle,
  Gavel,
  Hotel,
  Security,
  CalendarToday,
  Email,
  EmojiEvents,
  Shield,
  ArrowBack,
} from '@mui/icons-material';

// Theme colors - Teal & Gold Palette matching About page
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
};

// Global CSS with responsive overrides
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .policies-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(40px,7vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .policies-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .policies-hero-i{max-width:1320px;margin:0 auto;position:relative}
  .policies-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .policies-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.8rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .policies-hero-h em{font-style:normal;color:${T.accentGold}}
  .policies-hero-p{font-size:clamp(.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:560px}
  
  .policies-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:clamp(20px,3vw,28px);box-shadow:${T.shadow};transition:all 0.3s ease}
  .policies-card:hover{transform:translateY(-6px);box-shadow:${T.shadowLg}}
  
  .policies-cta-w{padding:0 clamp(16px,4vw,64px) 48px;background:${T.offWhite}}
  .policies-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(24px,4vw,52px) clamp(24px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .policies-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .policies-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .policies-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.2rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .policies-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .policies-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .policies-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  
  .policies-footer{border-top:1px solid ${T.border};background:${T.white};padding:28px clamp(16px,4vw,64px);margin-top:40px}
  .policies-fi{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
  .policies-f-copy{font-size:12px;color:${T.textMuted};letter-spacing:.01em}

  /* ─── RESPONSIVE OVERRIDES ─── */
  @media (max-width: 1024px) {
    .policies-hero-i { text-align: center; }
    .policies-hero-p { max-width: 100%; margin: 0 auto; }
  }
  @media (max-width: 768px) {
    .policies-hero { padding: 32px 16px 24px; }
    .policies-hero-h { font-size: 1.8rem; }
    .policies-hero-p { font-size: 0.9rem; }
    .policies-card { padding: 16px; }
    .policies-cta { flex-direction: column; text-align: center; padding: 28px 20px; }
    .policies-cta-btn { width: 100%; justify-content: center; }
    .policies-fi { flex-direction: column; text-align: center; gap: 12px; }
  }
  @media (max-width: 480px) {
    .policies-hero { padding: 24px 12px 20px; }
    .policies-hero-h { font-size: 1.6rem; }
    .policies-hero-lbl { font-size: 10px; padding: 4px 12px; }
    .policies-cta-h { font-size: 1.2rem; }
  }

  @keyframes spin{to{transform:rotate(360deg)}}
`;

const RegistrationPoliciesPage = () => {
  const [expandedSection, setExpandedSection] = useState('cancellation');
  const navigate = useNavigate();
  const { slug } = useParams();

  const getEventId = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const eventIdFromQuery = searchParams.get('eventId');
    if (eventIdFromQuery) return eventIdFromQuery;
    if (slug && slug.length === 24) return slug;
    return null;
  };

  const eventId = getEventId();

  const handleExpandSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <>
      <style>{css}</style>
      
      <div style={{ minHeight: '100vh', background: T.offWhite, fontFamily: "'Inter', sans-serif" }}>
        
        {/* Hero Section */}
        <section className="policies-hero">
          <div className="policies-hero-i">
            <div>
              <div className="policies-hero-lbl">
                <EmojiEvents size={13} style={{ fontSize: 13 }} />
                AICMSN 2026 • Lisbon, Portugal
              </div>
              <h1 className="policies-hero-h">
                Registration <em>Policies</em>
              </h1>
              <p className="policies-hero-p">
                Clear guidelines for cancellations, refunds, replacements, and important terms 
                to ensure a smooth and transparent registration process for all participants.
              </p>
            </div>
          </div>
        </section>

        <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
          
          <Stack spacing={{ xs: 3, md: 4 }}>
            {/* 1. Cancellation Policy - Collapsible Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: `1px solid ${T.border}`,
                  boxShadow: T.shadow,
                  transition: 'all 0.3s',
                  '&:hover': { boxShadow: T.shadowMd },
                }}
              >
                <Box
                  sx={{
                    background: T.gradPrimary,
                    p: { xs: 2.5, sm: 3, md: 3.5 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                  onClick={() => handleExpandSection('cancellation')}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: T.white,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                    }}
                  >
                    <CancelPresentation sx={{ color: T.white }} /> Cancellation Policy
                  </Typography>
                  <IconButton sx={{ color: T.white }}>
                    {expandedSection === 'cancellation' ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSection === 'cancellation'}>
                  <Box sx={{ p: { xs: 2.5, sm: 3, md: 5 } }}>
                    <Alert
                      severity="info"
                      sx={{
                        mb: 4,
                        borderRadius: 2,
                        background: T.softTeal,
                        borderLeft: `4px solid ${T.primary}`,
                        color: T.textMid,
                        fontSize: { xs: '0.85rem', md: '1rem' },
                      }}
                      icon={<Shield sx={{ color: T.primary }} />}
                    >
                      All cancellations must be notified <strong>in writing</strong> to the summit secretary.
                    </Alert>

                    <TableContainer sx={{ mb: 5, borderRadius: 2, overflow: 'auto' }}>
                      {/* FIX: Use size="medium" instead of an object */}
                      <Table size="medium">
                        <TableHead>
                          <TableRow sx={{ background: T.softTeal }}>
                            <TableCell sx={{ fontWeight: 700, width: '40%', color: T.text, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Period</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: T.text, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Refund</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: T.text, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Details</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ color: T.textMid, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>More than 90 days before the event</TableCell>
                            <TableCell align="center">
                              <Chip
                                label="80% Refund"
                                sx={{ background: T.gradPrimary, color: T.white, fontWeight: 600, fontSize: { xs: '0.65rem', md: '0.75rem' }, height: { xs: 24, md: 32 } }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: T.textMid, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>80% of registration fees refunded</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: T.textMid, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>60 to 90 days before the event</TableCell>
                            <TableCell align="center">
                              <Chip
                                label="50% Refund"
                                sx={{ background: T.gradGold, color: T.navyDeep, fontWeight: 600, fontSize: { xs: '0.65rem', md: '0.75rem' }, height: { xs: 24, md: 32 } }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: T.textMid, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>50% of registration fees refunded</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: T.textMid, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Less than 60 days before the event</TableCell>
                            <TableCell align="center">
                              <Chip
                                label="No Refund"
                                sx={{ background: T.accentTealDark, color: T.white, fontWeight: 600, fontSize: { xs: '0.65rem', md: '0.75rem' }, height: { xs: 24, md: 32 } }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: T.textMid, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Speaker not eligible for refund</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: T.textMid, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                              <Hotel sx={{ mr: 1, verticalAlign: 'middle', fontSize: { xs: 14, md: 16 }, color: T.accentGold }} />
                              Hotel Cancellation
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label="No Refund"
                                sx={{ background: T.accentTealDark, color: T.white, fontWeight: 600, fontSize: { xs: '0.65rem', md: '0.75rem' }, height: { xs: 24, md: 32 } }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: T.textMid, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>After 60 days deadline — no refund</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Stack spacing={2}>
                      <Alert severity="warning" icon={<AttachMoney sx={{ color: T.accentGold }} />} sx={{ borderRadius: 2, background: `${T.accentGold}10`, borderLeft: `4px solid ${T.accentGold}`, fontSize: { xs: '0.85rem', md: '1rem' } }}>
                        Service charges for cancellation will be borne by the participant.
                      </Alert>
                      <Alert severity="warning" icon={<AttachMoney sx={{ color: T.accentGold }} />} sx={{ borderRadius: 2, background: `${T.accentGold}10`, borderLeft: `4px solid ${T.accentGold}`, fontSize: { xs: '0.85rem', md: '1rem' } }}>
                        All bank/transaction charges during refund will be paid by the participant.
                      </Alert>
                      <Alert severity="error" icon={<Warning sx={{ color: T.accentTealDark }} />} sx={{ borderRadius: 2, background: `${T.accentTealDark}10`, borderLeft: `4px solid ${T.accentTealDark}`, fontSize: { xs: '0.85rem', md: '1rem' } }}>
                        Discounted fees are <strong>non-refundable</strong> and will be transferred to a future event.
                      </Alert>
                      <Alert severity="info" sx={{ borderRadius: 2, background: T.softTeal, borderLeft: `4px solid ${T.primary}`, color: T.textMid, fontSize: { xs: '0.85rem', md: '1rem' } }}>
                        All refunds will be processed only after the event concludes.
                      </Alert>
                    </Stack>
                  </Box>
                </Collapse>
              </Paper>
            </motion.div>

            {/* 2. Personal Reasons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  borderRadius: 4,
                  border: `1px solid ${T.border}`,
                  boxShadow: T.shadow,
                  transition: 'all 0.3s',
                  '&:hover': { boxShadow: T.shadowMd, transform: 'translateY(-2px)' },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    color: T.navy,
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 36, md: 40 },
                      height: { xs: 36, md: 40 },
                      borderRadius: '12px',
                      background: `${T.accentGold}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PersonOff sx={{ color: T.accentGold, fontSize: { xs: 20, md: 24 } }} />
                  </Box>
                  Personal Reason Exemption
                </Typography>
                <Typography variant="body1" sx={{ color: T.textMid, mb: 3, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  No refund or compensation will be provided if you are unable to attend due to personal reasons.
                </Typography>
                <Grid container spacing={2}>
                  {["Travel difficulties", "Visa problems", "Health issues", "Financial default"].map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <Chip
                        label={item}
                        variant="outlined"
                        sx={{
                          width: '100%',
                          justifyContent: 'flex-start',
                          py: 1.5,
                          borderColor: `${T.accentGold}40`,
                          color: T.textMid,
                          fontSize: { xs: '0.75rem', md: '0.9rem' },
                          '&:hover': { background: `${T.accentGold}10` },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </motion.div>

            {/* 3. Attendee Replacement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  borderRadius: 4,
                  border: `1px solid ${T.border}`,
                  boxShadow: T.shadow,
                  transition: 'all 0.3s',
                  '&:hover': { boxShadow: T.shadowMd, transform: 'translateY(-2px)' },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    color: T.navy,
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 36, md: 40 },
                      height: { xs: 36, md: 40 },
                      borderRadius: '12px',
                      background: `${T.primaryLight}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SwapHoriz sx={{ color: T.primaryLight, fontSize: { xs: 20, md: 24 } }} />
                  </Box>
                  Attendee Replacement Policy
                </Typography>
                <Alert
                  severity="info"
                  sx={{
                    mb: 4,
                    borderRadius: 2,
                    background: T.softTeal,
                    borderLeft: `4px solid ${T.primary}`,
                    color: T.textMid,
                    fontSize: { xs: '0.85rem', md: '1rem' },
                  }}
                  icon={<Shield sx={{ color: T.primary }} />}
                >
                  You can replace yourself with another person if requested <strong>at least 30 days</strong> before the event.
                </Alert>
                <List>
                  {[
                    "The substitute must have relevant background and research aligned with the summit theme",
                    "Prior approval from the summit committee is mandatory",
                    "Must inform the summit secretary via email promptly"
                  ].map((text, i) => (
                    <ListItem key={i} sx={{ px: 0, py: { xs: 1, md: 1.5 } }}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: T.primary, fontSize: { xs: 20, md: 24 } }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={text}
                        primaryTypographyProps={{ sx: { color: T.textMid, fontSize: { xs: '0.85rem', md: '0.95rem' } } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </motion.div>

            {/* 4. Force Majeure & Other Terms */}
            <Grid container spacing={{ xs: 3, md: 4 }}>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, sm: 4, md: 5 },
                      borderRadius: 4,
                      border: `1px solid ${T.border}`,
                      boxShadow: T.shadow,
                      height: '100%',
                      transition: 'all 0.3s',
                      '&:hover': { boxShadow: T.shadowMd, transform: 'translateY(-2px)' },
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        color: T.navy,
                        fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 36, md: 40 },
                          height: { xs: 36, md: 40 },
                          borderRadius: '12px',
                          background: `${T.primary}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Gavel sx={{ color: T.primary, fontSize: { xs: 20, md: 24 } }} />
                      </Box>
                      Force Majeure
                    </Typography>
                    <Typography variant="body1" sx={{ color: T.textMid, lineHeight: 1.7, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                      Synergia Summits is not responsible for any refund or compensation if the event is affected by circumstances beyond control such as natural disasters, epidemics, war, strikes, etc.
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, sm: 4, md: 5 },
                      borderRadius: 4,
                      border: `1px solid ${T.border}`,
                      boxShadow: T.shadow,
                      height: '100%',
                      transition: 'all 0.3s',
                      '&:hover': { boxShadow: T.shadowMd, transform: 'translateY(-2px)' },
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        color: T.navy,
                        fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 36, md: 40 },
                          height: { xs: 36, md: 40 },
                          borderRadius: '12px',
                          background: `${T.accentGold}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Security sx={{ color: T.accentGold, fontSize: { xs: 20, md: 24 } }} />
                      </Box>
                      Media Consent
                    </Typography>
                    <Typography variant="body1" sx={{ color: T.textMid, lineHeight: 1.7, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                      By attending, you consent to photographs, video, and audio recordings being used for promotional and event-related purposes on website and social media.
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            </Grid>

            {/* Contact Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Paper
                elevation={0}
                sx={{
                  background: T.gradNavy,
                  borderRadius: 4,
                  textAlign: 'center',
                  py: { xs: 4, sm: 5, md: 6 },
                  px: { xs: 3, sm: 4, md: 6 },
                  position: 'relative',
                  overflow: 'hidden',
                  border: `1px solid rgba(59,188,174,0.28)`,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    background: `radial-gradient(circle, ${T.primaryLight}20, transparent)`,
                    borderRadius: '50%',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -50,
                    left: -50,
                    width: 150,
                    height: 150,
                    background: `radial-gradient(circle, ${T.accentGold}15, transparent)`,
                    borderRadius: '50%',
                  }}
                />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: T.white,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      fontFamily: "'Playfair Display', serif",
                      fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                    }}
                  >
                    <Email sx={{ color: T.accentGoldLight }} />
                    Need Help or Clarification?
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: T.textLight,
                      mb: 4,
                      maxWidth: 400,
                      mx: 'auto',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                  >
                    Contact the Summit Secretary for any queries regarding registration or cancellation.
                  </Typography>
                  <Button
                    component={Link}
                    to={`/contact${eventId ? `?eventId=${eventId}` : ''}`}
                    variant="contained"
                    size="large"
                    sx={{
                      background: T.gradGold,
                      color: T.navyDeep,
                      px: { xs: 3, sm: 4, md: 5 },
                      py: { xs: 1.2, md: 1.5 },
                      borderRadius: '50px',
                      fontWeight: 700,
                      fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                      textTransform: 'none',
                      boxShadow: T.shadowGold,
                      textDecoration: 'none',
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: T.shadowLg,
                        background: T.gradGoldReverse,
                      },
                      transition: 'all 0.3s',
                    }}
                  >
                    Contact Us
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          </Stack>
        </Container>
      </div>
    </>
  );
};

export default RegistrationPoliciesPage;